import { useState } from "react";
import { Loader2, Download, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { importAnilist, importMal } from "../lib/api";

export default function ImportPage() {
  const [source, setSource] = useState("anilist");
  const [username, setUsername] = useState("");
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error("Enter a username first");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const fn = source === "anilist" ? importAnilist : importMal;
      const data = await fn(username.trim(), Number(limit) || 50);
      setResult(data);
      toast.success(`Imported ${data.added_count} anime`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Import failed";
      toast.error(msg);
      setResult({ error: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1000px] px-6 py-10 md:px-10">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 06</p>
      <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-8xl">
        Bulk<br/>Import
      </h1>
      <p className="mt-4 max-w-lg text-sm text-neutral-400">
        Pull your entire anime history from AniList or MyAnimeList in one shot. Public lists only — no auth needed.
      </p>

      {/* Source selector */}
      <div className="mt-10 flex border border-white/15">
        <button
          data-testid="source-anilist"
          onClick={() => setSource("anilist")}
          className={`flex-1 border-r border-white/15 py-4 font-mono text-[11px] uppercase tracking-widest ${source === "anilist" ? "bg-[#FF3B30] text-white" : "bg-transparent text-neutral-400 hover:text-white"}`}
        >
          AniList
        </button>
        <button
          data-testid="source-mal"
          onClick={() => setSource("mal")}
          className={`flex-1 py-4 font-mono text-[11px] uppercase tracking-widest ${source === "mal" ? "bg-[#FF3B30] text-white" : "bg-transparent text-neutral-400 hover:text-white"}`}
        >
          MyAnimeList
        </button>
      </div>

      <form onSubmit={handleImport} className="mt-6 space-y-4 border border-white/15 p-6">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            {source === "anilist" ? "AniList username" : "MyAnimeList username"}
          </span>
          <input
            data-testid="import-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={source === "anilist" ? "e.g. Josh" : "e.g. TypeMoon"}
            className="mt-2 w-full border border-white/15 bg-black px-3 py-3 text-white focus:border-[#FF3B30] focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Max entries to import (1–100)
          </span>
          <input
            data-testid="import-limit"
            type="number"
            min={1}
            max={100}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            className="mt-2 w-full border border-white/15 bg-black px-3 py-3 text-white focus:border-[#FF3B30] focus:outline-none"
          />
          <span className="mt-2 block font-mono text-[10px] uppercase tracking-widest text-neutral-600">
            Each entry needs a Jikan lookup (~0.5s) so large imports take time.
          </span>
        </label>
        <button
          data-testid="import-submit"
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 border border-white/15 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-[#D62828] disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Import
        </button>
      </form>

      {loading && (
        <p className="mt-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500">
          <Loader2 className="h-3 w-3 animate-spin" /> Fetching list & pulling details from MyAnimeList...
        </p>
      )}

      {result && !result.error && (
        <div className="mt-6 border border-emerald-500/40 bg-emerald-500/5 p-6" data-testid="import-result">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <h2 className="font-display text-2xl uppercase text-white">Done</h2>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-6 md:grid-cols-4">
            <Stat label="Added" value={result.added_count} accent />
            <Stat label="Skipped" value={result.skipped_count} />
            <Stat label="Processed" value={result.processed} />
            <Stat label="Total on list" value={result.total_source} />
          </div>
          {result.added_titles?.length > 0 && (
            <div className="mt-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Added:</p>
              <ul className="mt-2 space-y-1">
                {result.added_titles.map((t) => (
                  <li key={t} className="font-mono text-xs text-white">· {t}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {result?.error && (
        <div className="mt-6 border border-red-500/40 bg-red-500/5 p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-white">{result.error}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`${accent ? "border-l-2 border-[#FF3B30] pl-4" : ""}`}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="mt-1 font-display text-4xl text-white">{value ?? "—"}</p>
    </div>
  );
}
